<template>
  <!-- DESCRIPTION AND CARE INSTRUCTIONS -->
  <!--
    Desktop shows these as an in-context accordion in the PDP right column
    (ECOM-2679), so the full-width block is rendered on mobile only.
  -->
  <div v-if="isMobile" class="-mx-5">
    <ProductDetailsNew
      :product="product"
      :product-order-number="productOrderNumber"
    />
  </div>

  <!--
    Recommendations are deferred (AgsLazy when-idle) and fetched client-side
    (fetch-on-client) for rendered-source-size/perf (ECOM-2626), wrapping the
    A/B winner-order stack below (ECOM-2759/ECOM-2760) so both concerns hold.
  -->
  <AgsLazy placeholder-ratio="4/1" when-idle>
    <!--
      RECOMMENDATION STACK — A/B winner order (ECOM-2759 desktop, ECOM-2760 mobile).
      Both winners lead with "Ähnliche Produkte" but disagree on the rest:
        <  1024px: Ähnliche Produkte → Das passt dazu → Zuletzt angesehen
        >= 1024px: Ähnliche Produkte → Zuletzt angesehen → Das passt dazu
      1024px is the PDP's own layout breakpoint (clothing-pdp.vue goes two-column
      at `lg`), so the desktop order applies exactly when the desktop layout does.

      The order is CSS-driven rather than branched on device state on purpose: the
      SSR payload stays user-agent independent, and resizing across the breakpoint
      reorders without remounting the carousels or refetching recommendations. DOM
      order is the mobile order, so only desktop diverges from reading order.

      No `gap` utility here, deliberately — a carousel that renders nothing (empty
      Odoscope response, control group, empty last-seen list) collapses to a
      zero-height slot, and a gap would leave a hole where it sits. Vertical rhythm
      stays on each carousel's own title spacing, as before.
    -->
    <div class="flex flex-col">
      <div class="order-1" data-test-id="recommendations_slot_similar_products">
        <ProductRecommendations
          v-if="product.id"
          :token="OdoscopeRecommendationsToken.SIMILAR_PRODUCTS"
          :title="$t('recommendations.title.similar_products')"
          :product-id="props.product.id"
          fetch-on-client
        />
      </div>

      <div
        class="order-2 lg:order-3"
        data-test-id="recommendations_slot_shop_the_look"
      >
        <ProductRecommendations
          v-if="product.id"
          :token="OdoscopeRecommendationsToken.SHOP_THE_LOOK"
          :title="$t('recommendations.title.shop_the_look')"
          :product-id="props.product.id"
          fetch-on-client
        />
      </div>

      <div
        class="order-3 lg:order-2"
        data-test-id="recommendations_slot_last_seen"
      >
        <LastSeenProductsRecommendations :product-id="product.id" />
      </div>

      <!--
        Disabled today (showAlsoPurchased is false) and not part of either test.
        Parked in a trailing slot so re-enabling it cannot drop it into the middle
        of the winner order.
      -->
      <div class="order-4">
        <ProductRecommendations
          v-if="product.id && showAlsoPurchased"
          :token="OdoscopeRecommendationsToken.ALSO_PURCHASED"
          :title="$t('recommendations.title.also_purchased')"
          :product-id="props.product.id"
          fetch-on-client
        />
      </div>
    </div>
  </AgsLazy>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-nuxt'
import ProductDetailsNew from '~/components/product/ProductDetailsNew.vue'
import { OdoscopeRecommendationsToken } from '#layers/base/constants/recommendations'
import LastSeenProductsRecommendations from '#layers/base/components/product/LastSeenProductsRecommendations.vue'
import ProductRecommendations from '~/components/product/ProductRecommendations.vue'
import { useDeviceType } from '~/composables/useDeviceType'

const { isMobile } = useDeviceType()

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  productOrderNumber: {
    type: String as PropType<string>,
    default: undefined,
  },
})

const showAlsoPurchased = false
</script>
